<script setup lang="ts">
import { computed } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Timeline from '@uiKit/elements/Timeline.vue'
import Button from '@uiKit/elements/Button.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chip from '@uiKit/elements/Chip.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import { formatDateTime } from '@shared/utils/dateTimeUtils'
import { formatFeeCalculation, formatVatRate } from '@shared/utils/feeUtils'
import { FEE_RECURRENCE_LABEL } from '@shared/constants/fees'
import type { FeeDefinitionVersion } from '../types/feeTypes'

const props = defineProps<{
  visible: boolean
  feeName: string
  /** Every version of one `(marketId, code)` pair, newest first. */
  versions: FeeDefinitionVersion[]
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

interface FeeVersionEntry {
  id: string
  calculation: string
  /** What the version before it charged — null on the original version. */
  previousCalculation: string | null
  vat: string
  recurrence: string
  validFrom: string
  validTo: string | null
  isInForce: boolean
  isActive: boolean
  isCustomisable: boolean
}

function toFeeCalculation(version: FeeDefinitionVersion): string {
  return formatFeeCalculation({
    value: version.defaultValue,
    valueType: version.valueType,
    valueBasis: version.valueBasis,
    unitBasis: version.unitBasis,
  })
}

// The API returns whole rows, not diffs, so a version's "change" is read by
// comparing it against the one it superseded — the next entry in the list.
const entries = computed<FeeVersionEntry[]>(() =>
  props.versions.map((version, index) => {
    const superseded = props.versions[index + 1]
    return {
      id: version.id,
      calculation: toFeeCalculation(version),
      previousCalculation: superseded ? toFeeCalculation(superseded) : null,
      vat: formatVatRate(version.isVatApplicable, version.vatPercentage),
      recurrence: FEE_RECURRENCE_LABEL[version.recurrence],
      validFrom: version.validFrom,
      validTo: version.validTo,
      isInForce: version.validTo === null,
      isActive: version.isActive,
      isCustomisable: version.isCustomisable,
    }
  }),
)
</script>

<template>
  <Dialog
    :visible="props.visible"
    :header="`Version history: ${props.feeName}`"
    width="820px"
    @update:visible="emit('update:visible', $event)"
  >
    <Box class="p-2">
      <Box v-if="props.isLoading" flex class="flex-column gap-3">
        <Skeleton v-for="row in 3" :key="row" height="5rem" />
      </Box>

      <Timeline v-else-if="entries.length" :value="entries" align="left" class="custom-timeline">
        <template #opposite="slotProps">
          <Text type="caption" class="timeline-date">
            {{ formatDateTime(slotProps.item.validFrom) }}
          </Text>
        </template>
        <template #content="slotProps">
          <Box class="history-card mb-5">
            <Box flex class="justify-content-between align-items-start gap-3 mb-3">
              <Box>
                <Text type="caption" class="modification-label block mb-1">
                  {{ slotProps.item.previousCalculation ? 'Superseded' : 'Original version' }}
                </Text>
                <Box flex class="align-items-center gap-2 flex-wrap">
                  <Text
                    v-if="slotProps.item.previousCalculation"
                    type="body"
                    color="muted"
                    class="line-through"
                  >
                    {{ slotProps.item.previousCalculation }}
                  </Text>
                  <PiIcon
                    v-if="slotProps.item.previousCalculation"
                    name="arrow-right"
                    class="text-muted"
                    size="xs"
                  />
                  <Text type="body" weight="bold">{{ slotProps.item.calculation }}</Text>
                </Box>
              </Box>
              <Chip
                v-if="slotProps.item.isInForce"
                label="IN FORCE"
                color="success"
                variant="tonal"
                size="small"
                dot
              />
            </Box>

            <Box class="grid mt-3 pt-3 timeline-detail-grid">
              <Box class="col-6 md:col-3">
                <Text type="caption" class="col-label block mb-1">VAT</Text>
                <Text type="body" color="muted">{{ slotProps.item.vat }}</Text>
              </Box>
              <Box class="col-6 md:col-3">
                <Text type="caption" class="col-label block mb-1">Recurrence</Text>
                <Text type="body" color="muted">{{ slotProps.item.recurrence }}</Text>
              </Box>
              <Box class="col-6 md:col-3">
                <Text type="caption" class="col-label block mb-1">Overridable</Text>
                <Text type="body" color="muted">
                  {{ slotProps.item.isCustomisable ? 'Yes' : 'No' }}
                </Text>
              </Box>
              <Box class="col-6 md:col-3">
                <Text type="caption" class="col-label block mb-1">Closed</Text>
                <Text type="body" color="muted">
                  {{ slotProps.item.validTo ? formatDateTime(slotProps.item.validTo) : '—' }}
                </Text>
              </Box>
            </Box>
          </Box>
        </template>
      </Timeline>

      <Box v-else flex class="flex-column align-items-center py-8">
        <PiIcon name="history" class="text-4xl mb-3 empty-icon" />
        <Text type="body" color="muted">No versions recorded for this fee yet.</Text>
      </Box>

      <Box flex class="justify-content-center mt-4">
        <Button label="Close" variant="text" @click="emit('update:visible', false)" />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.text-muted {
  color: var(--p-text-muted-color);
}

.modification-label {
  color: var(--p-primary-500);
}

.col-label {
  color: var(--p-text-muted-color);
}

.line-through {
  text-decoration: line-through;
}

.history-card {
  background: var(--p-surface-50);
  padding: 1rem;
  border-radius: var(--p-border-radius-lg);
  border: 1px solid var(--p-surface-200);
  transition: all 0.2s ease;
}

.history-card:hover {
  background: var(--p-surface-0);
  border-color: var(--p-primary-300);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.timeline-detail-grid {
  border-top: 1px solid var(--p-surface-200);
}

.empty-icon {
  opacity: 0.2;
  color: var(--p-text-muted-color);
}

.custom-timeline :deep(.p-timeline-event-marker) {
  border-color: var(--p-primary-500);
  background-color: var(--p-surface-0);
  border-width: 2px;
}

.custom-timeline :deep(.p-timeline-event-connector) {
  background-color: var(--p-surface-200);
}
</style>
